import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import {
  PRE_VISIT_QUESTION_TYPE,
  PRE_VISIT_QUESTION_TYPE_ICON,
} from '@/constants/preVisitQuestionTypes';
import { resolveLocalizedText } from '@/utils/localizedText';
import type { EditableQuestionRow } from './preVisitFormEditorState';

interface PreVisitQuestionRowProps {
  row: EditableQuestionRow;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onRequestRemoval: () => void;
}

export function PreVisitQuestionRow({
  row,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onEdit,
  onRequestRemoval,
}: PreVisitQuestionRowProps) {
  const { t, i18n } = useTranslation('consultations');
  const label = resolveLocalizedText(row.labelEn, row.labelPl, i18n.language);
  const isSectionHeading = row.questionType === PRE_VISIT_QUESTION_TYPE.sectionHeading;

  return (
    <div
      data-pre-visit-question-row
      className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3 border border-zooborder rounded-xl bg-zoowhite"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1 basis-56">
        <span
          className={
            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ' +
            (isSectionHeading
              ? 'bg-zooindigo text-zoowhite'
              : 'bg-zoovpalelavender text-zooprimary')
          }
        >
          <Icon name={PRE_VISIT_QUESTION_TYPE_ICON[row.questionType]} />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className={
              'text-sm line-clamp-2 break-words ' +
              (isSectionHeading
                ? 'font-bold uppercase tracking-wider text-zoomuted'
                : 'font-semibold text-zooindigo')
            }
          >
            {label.length === 0 ? t('preVisitEditor.untitledQuestion') : label}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
            <span className="text-xs text-zoomuted">
              {t('preVisitEditor.questionType.' + row.questionType)}
            </span>
            {row.isRequired ? (
              <Badge tone="warning" size="sm">
                {t('preVisitEditor.required')}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0 ml-auto">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('preVisitEditor.moveUp')}
          disabled={isFirst}
          onClick={onMoveUp}
          className="w-8 h-8"
        >
          <Icon name="ChevronDown" className="rotate-180" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('preVisitEditor.moveDown')}
          disabled={isLast}
          onClick={onMoveDown}
          className="w-8 h-8"
        >
          <Icon name="ChevronDown" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('preVisitEditor.edit')}
          onClick={onEdit}
          className="w-8 h-8"
        >
          <Icon name="Edit" />
        </Button>
        <Button
          variant="destructiveGhost"
          size="icon"
          aria-label={t('preVisitEditor.deactivate')}
          onClick={onRequestRemoval}
          className="w-8 h-8"
        >
          <Icon name="Trash" />
        </Button>
      </div>
    </div>
  );
}
